import { useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Center, Text, Spinner, VStack } from "@chakra-ui/react"
import { useFeatureEnabled } from "../hooks/useFeatureToggles"
import { useActiveOrganization, useOrganizations } from "../auth/AuthProvider"
import { NoOrganizationsEmptyState } from "./organization/NoOrganizationsEmptyState"
import { FeatureResult } from "@hydrologiq/gql-experience/graphql"

/**
 * Dashboard home page component that handles fallback logic when dashboard_home_page feature is disabled.
 *
 * Fallback priority:
 * 1. URL path for valid section
 * 2. User preference for preferred section (from getPreferences.feature)
 * 3. First enabled feature from priority list
 * 4. Default "ask support" page/section
 */
export const DashboardHome = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeOrganization, features, preferredSection } = useActiveOrganization()
  const { organizations } = useOrganizations()
  const isDashboardHomeEnabled = useFeatureEnabled("dashboard_home_page")
  useEffect(() => {
    // If dashboard home page is enabled, don't redirect
    if (isDashboardHomeEnabled || !activeOrganization || !features) {
      return
    }

    // If we're not on the root path, don't interfere
    if (location.pathname !== "/") {
      return
    }

    // Implement fallback logic
    const findFallbackRoute = () => {
      // 1. Check URL path for valid section (already handled by routing)

      // 2. Check user preferred section
      if (preferredSection) {
        const isEnabled = features.some(
          (f) =>
            (f.name.includes(preferredSection) ||
              f.name.toLowerCase().includes(preferredSection.toLowerCase())) &&
            f.enabled
        )
        if (isEnabled) {
          return `/${preferredSection}`
        }
      }

      // 3. Use first enabled feature from priority list
      const sectionPriority = ["dashboards", "assets", "customers", "systems", "projects", "planner", "orders", "filling"]

      if (features) {
        let sectionFeature: FeatureResult | undefined
        // Find the first enabled section based on resolved feature toggles
        for (const section of sectionPriority) {

          // Check if there's a corresponding feature toggle for this section
          sectionFeature = features.find(
            (f) => f.name.includes(section) || f.name.toLowerCase().includes(section.toLowerCase())
          )

          // If no specific feature toggle exists or if it's enabled, use this section
          if (sectionFeature && sectionFeature.enabled) {
            return `/${section}`
          }
        }

        // 4. First feature
        if (features.length > 0) {
          return `/${features[0].name}`
        }

        // 5. Default fallback - could be a support page or contact form
        // For now, redirect to assets as a safe default
        return "/assets"
      }
    }

    const fallbackRoute = findFallbackRoute()
    if (organizations.length === 0) {
      return
    }
    if (fallbackRoute) {
      navigate(fallbackRoute, { replace: true })
    }
  }, [isDashboardHomeEnabled, activeOrganization, location.pathname, navigate, features, preferredSection])

  // Show loading while determining redirect
  if (!activeOrganization && !organizations.length) {
    return (
      <Center height="calc(100vh - 120px)">
        <VStack gap={4}>
          <Spinner size="lg" color="primary" />
          <Text color="primary">Loading dashboard...</Text>
        </VStack>
      </Center>
    )
  }
  if (!organizations.length) {
    return <NoOrganizationsEmptyState />
  }
  // Show default home page if feature is enabled
  if (isDashboardHomeEnabled && organizations.length) {
    return (
      <Center height="calc(100vh - 120px)" flexDirection="column" gap={4} color="primary">
        <Text fontSize="2xl" fontWeight="bold">
          Welcome to Hydrologiq Hub
        </Text>
        <Text>
          {activeOrganization?.organization.name
            ? `Organization: ${activeOrganization.organization.name}`
            : "Select an organization to get started"}
        </Text>
        <Text fontSize="sm" color="gray.500">
          Choose a section from the sidebar to begin
        </Text>
      </Center>
    )
  }

  // This should not be reached due to the redirect effect, but provide fallback
  return (
    <Center height="calc(100vh - 120px)">
      <Text color="primary">Redirecting...</Text>
    </Center>
  )
}
